
/**
 * Module dependencies.
 */

import { MenuGroup, useFooterMenu } from 'src/core/menu';
import { color, media, units } from 'src/styles/utils';
import { dividerColor } from './footer-theme';
import { pageContainer, pageHorizontalPadding } from 'src/components/core/layout/page-section';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useTranslate } from 'src/core/utils/translator';
import Credits from './credits';
import FooterAccordion from './footer-accordion';
import FooterActionLink from './footer-action-link';
import FooterColumn from './footer-column';
import FooterLinkList from './footer-link-list';
import Logos from './logos';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import Showrooms from './showrooms';
import SocialLinks from './social-links';
import Svg from 'src/components/core/svg';
import logoIcon from 'src/assets/svg/logo.svg';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * Contacts column id.
 */

const contactsId = 'footer-contacts';

/**
 * Logo width.
 *
 * `Svg` forwards `size` to the dom, so it has to be a plain string: an
 * interpolation built with `units` would reach react as a function.
 */

const logoWidth = '105px';

/**
 * `Section` styled component.
 */

const Section = styled.footer`
  ${pageHorizontalPadding}

  background-color: ${color('brown200')};
  padding-bottom: ${units(6)};
  padding-top: ${units(6)};

  ${media.min('md')`
    padding-bottom: ${units(10)};
    padding-top: ${units(10)};
  `}

  @media print {
    display: none;
  }
`;

/**
 * `Container` styled component.
 */

const Container = styled.div`
  ${pageContainer}

  display: flex;
  flex-direction: column;
  gap: ${units(5)};
`;

/**
 * `LogoLink` styled component.
 */

const LogoLink = styled(RouterLink)`
  align-self: flex-start;
  color: ${color('white')};
  display: block;
  outline: none;
  transition: color ${theme('animations.fastTransition')};

  &:focus-visible,
  &:hover {
    color: ${color.transparentize('white', 0.7)};
  }
`;

/**
 * `DesktopLinks` styled component.
 */

const DesktopLinks = styled.div`
  display: none;

  ${media.min('md')`
    display: flex;
    gap: ${units(3)};
  `}
`;

/**
 * `LogoColumn` styled component.
 */

const LogoColumn = styled.div`
  flex: none;
  width: ${units(28.375)};
`;

/**
 * `LinkColumn` styled component.
 */

const LinkColumn = styled(FooterColumn)`
  flex: 1 1 0;
`;

/**
 * `MobileLinks` styled component.
 */

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(5)};

  ${media.min('md')`
    display: none;
  `}
`;

/**
 * `ShowroomsRow` styled component.
 */

const ShowroomsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(5)};

  ${media.min('md')`
    flex-direction: row;
    gap: ${units(3)};
    justify-content: space-between;
  `}
`;

/**
 * `ShowroomsWrapper` styled component.
 */

const ShowroomsWrapper = styled.div`
  ${media.min('md')`
    flex: 1 1 0;
    min-width: 0;
  `}
`;

/**
 * `Divider` styled component.
 */

const Divider = styled.hr`
  background-color: ${dividerColor};
  border: 0;
  height: 1px;
  margin: 0;
  width: 100%;
`;

/**
 * `DesktopDivider` styled component.
 */

const DesktopDivider = styled(Divider)`
  display: none;

  ${media.min('md')`
    display: block;
  `}
`;

/**
 * `ContactsLead` styled component.
 */

const ContactsLead = styled.div`
  color: ${color('white')};
  font-size: 14px;
  line-height: 1.5;
  padding: ${units(1)} 0;

  p {
    margin: 0;
  }
`;

/**
 * `ContactsAction` styled component.
 */

const ContactsAction = styled.div`
  padding: ${units(1)} 0;
`;

/**
 * `Footer` component.
 */

const Footer = (): ReactElement => {
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const menu = useFooterMenu();
  const contacts = (
    <>
      <RawHtml element={ContactsLead}>
        {translate('footer:contacts.lead')}
      </RawHtml>

      <ContactsAction>
        <FooterActionLink href={routeResolver('contacts')}>
          {translate('footer:contacts.button')}
        </FooterActionLink>
      </ContactsAction>
    </>
  );

  const logo = (
    <LogoLink
      aria-label={'Revigrés'}
      href={routeResolver('home')}
    >
      <Svg
        icon={logoIcon}
        size={logoWidth}
      />
    </LogoLink>
  );

  return (
    <Section>
      <Container>
        <nav aria-label={translate('footer:navLabel')}>
          <DesktopLinks>
            <LogoColumn>
              {logo}
            </LogoColumn>

            {map(menu, ({ links, title }: MenuGroup) => (
              <LinkColumn
                key={title}
                links={links}
                title={title}
              />
            ))}

            <LinkColumn title={translate('menu:contacts')}>
              {contacts}
            </LinkColumn>
          </DesktopLinks>

          <MobileLinks>
            {logo}

            <div>
              {map(menu, ({ links, title }: MenuGroup, index: number) => (
                <FooterAccordion
                  id={`footer-column-${index}`}
                  key={title}
                  title={title}
                >
                  <FooterLinkList links={links} />
                </FooterAccordion>
              ))}

              <FooterAccordion
                id={contactsId}
                title={translate('menu:contacts')}
              >
                {contacts}
              </FooterAccordion>
            </div>
          </MobileLinks>
        </nav>

        <DesktopDivider />

        <ShowroomsRow>
          <ShowroomsWrapper>
            <Showrooms />
          </ShowroomsWrapper>

          <SocialLinks />
        </ShowroomsRow>

        <Divider />

        <Logos />

        <Divider />

        <Credits />
      </Container>
    </Section>
  );
};

/**
 * Export `Footer` component.
 */

export default Footer;
